import React, { useState, useEffect } from "react";
import "./ModalEditarProduto.scss";
//MUI
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment";
import { momentGuardado } from "../../../shared/util/datasGuardadas";
import { useLoading } from "../../../shared/context/LoadingContext";

const UNIDADES_MEDIDA = [
  { value: "UN", label: "Unidade" },
  { value: "KG", label: "Quilograma (kg)" },
  { value: "M", label: "Metro (m)" },
  { value: "L", label: "Litro (l)" },
  { value: "M3", label: "Metro Cúbico (m³)" },
  { value: "M2", label: "Metro Quadrado (m²)" },
  { value: "CX", label: "Caixa" },
];

function ModalEditarProduto({
  handleCloseModalEditar,
  sendRequest,
  id,
  open,
  clinicaId,
  auth,
  categoriasStock,
  recarregar,
  qtdIdeal,
}) {
  const { startLoading, stopLoading } = useLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [nomeProduto, setNomeProduto] = useState("");
  const [categoria, setCategoria] = useState("");
  const [unidadeMedida, setUnidadeMedida] = useState("UN");
  const [quantidadeIdeal, setQuantidadeIdeal] = useState(0);
  const [produtoLotes, setProdutoLotes] = useState([]);
  // Valores para campos anteriormente em componentes personalizados
  const [classificacao, setClassificacao] = useState("PA");

  /* Unidades da clínica e o mínimo de cada uma. Vazias nas clínicas de unidade
     única — e aí nada disto aparece no ecrã. Guardado como objeto
     {idDaUnidade: valor} para o campo poder ficar em branco, que é a maneira de
     dizer «usa o mínimo da clínica». */
  const [unidades, setUnidades] = useState([]);
  const [minimosPorUnidade, setMinimosPorUnidade] = useState({});

  useEffect(() => {
    const fetchProduto = async () => {
      startLoading();
      try {
        // Buscar dados do produto
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/produtos/${id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        );

        setNomeProduto(responseData.produto.nome);
        setCategoria(responseData.produto.categoriaStock);
        setUnidadeMedida(responseData.produto.unidadeMedida || "UN");
        setClassificacao(responseData.produto.classificacao || "PA");

        // Definir a quantidade ideal inicial
        setQuantidadeIdeal(qtdIdeal || 0);

        // Buscar lotes deste produto do estoque
        const stockResponse = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/clinica/${clinicaId}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        );

        // Encontrar lotes deste produto
        if (stockResponse && stockResponse.stock) {
          const produtoNoStock = stockResponse.stock.stock.find(
            (item) => item.produto._id === id,
          );

          if (produtoNoStock) {
            setProdutoLotes(
              produtoNoStock.lotes.map((lote) => ({
                ...lote,
                // momentGuardado e não moment: a validade é um dia, lida em
                // UTC. Com `moment()` o picker abria no dia anterior e a
                // gravação seguinte fixava esse recuo.
                dataValidade: momentGuardado(lote.dataValidade),
              })),
            );

            const porUnidade = {};
            for (const m of produtoNoStock.minimosPorUnidade || []) {
              porUnidade[m.estabelecimento] = m.quantidadeIdeal;
            }
            setMinimosPorUnidade(porUnidade);
          }
        }

        /* À parte: numa clínica de unidade única esta chamada não traz nada de
           útil, e uma falha aqui não deve impedir a edição do produto. */
        try {
          const resUnidades = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/clinica/${clinicaId}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          );
          setUnidades(resUnidades.estabelecimentos || []);
        } catch (err) {
          console.error("Erro ao carregar unidades:", err);
        }
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchProduto();
  }, [sendRequest, clinicaId, auth.token, qtdIdeal, id]);

  const handleSubmit = async () => {
    startLoading();
    try {
      // Atualizar dados do produto
      const formData = new FormData();
      formData.append("categoriaStock", categoria);
      formData.append("nome", nomeProduto);
      formData.append("unidadeMedida", unidadeMedida);
      formData.append("classificacao", classificacao);
      formData.append("atualizadoPor", auth.userId);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/produtos/${id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      /* O mínimo da clínica e os das unidades vão no mesmo pedido: são o mesmo
         assunto, e separá-los deixava metade gravada se o segundo falhasse.
         A lista vai COMPLETA — é assim que um campo limpo no ecrã apaga mesmo
         o mínimo dessa unidade. */
      const minimosParaEnviar = unidades
        .map((u) => ({
          estabelecimento: u._id,
          quantidadeIdeal: minimosPorUnidade[u._id],
        }))
        .filter(
          (m) =>
            m.quantidadeIdeal !== "" &&
            m.quantidadeIdeal !== null &&
            m.quantidadeIdeal !== undefined,
        );

      if (quantidadeIdeal !== qtdIdeal || unidades.length > 1) {
        const formData2 = new FormData();
        formData2.append("produtoId", id);
        formData2.append("novaQuantidadeIdeal", quantidadeIdeal);
        if (unidades.length > 1) {
          formData2.append(
            "minimosPorUnidade",
            JSON.stringify(minimosParaEnviar),
          );
        }
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/atualizarQuantidadeIdeal/${clinicaId}`,
          "PATCH",
          formData2,
          {
            Authorization: "Bearer " + auth.token,
          },
        );
      }

      // Atualizar datas de validade dos lotes
      const lotesAtualizados = produtoLotes.map((lote) => ({
        _id: lote._id,
        lote: lote.lote,
        quantidade: lote.quantidade,
        dataValidade: lote.dataValidade
          ? lote.dataValidade.format("YYYY-MM-DD")
          : null,
        precoCompra: lote.precoCompra || 0,
        fornecedor: lote.fornecedor?._id || lote.fornecedor || null,
        armazem: lote.armazem || "N/A",
        localizacao: lote.localizacao || "N/A",
      }));
      const loteUpdateData = {
        produtoId: id,
        lotes: lotesAtualizados,
      };

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/atualizarLotes/${clinicaId}`,
        "PATCH",
        JSON.stringify(loteUpdateData),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      recarregar();
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
      handleCloseModalEditar();
    }
  };

  const preHandleSubmit = () => {
    let mensagemDeErro = "";
    if (!nomeProduto) {
      mensagemDeErro = "Por favor introduza o nome do Produto!";
      return;
    }

    if (!categoria) {
      mensagemDeErro = "Por favor introduza a categoria de Stock do produto!";
      return;
    }

    if (quantidadeIdeal < 0) {
      mensagemDeErro = "O stock mínimo não pode ser negativa!";
      return;
    }

    if (mensagemDeErro) {
      setValidationError(mensagemDeErro);
    } else if (!mensagemDeErro && validationError) {
      setValidationError("");
    }

    if (!isSubmitting && !mensagemDeErro) {
      setIsSubmitting(true);
      handleSubmit();
    }
  };

  // Função para atualizar a data de validade de um lote
  const handleLoteValidade = (date, index) => {
    const updatedLotes = [...produtoLotes];
    updatedLotes[index].dataValidade = date;
    setProdutoLotes(updatedLotes);
  };

  const handleQuantidadeIdealChange = (e) => {
    const value = e.target.value;
    // Converter para número e garantir que não seja negativo
    setQuantidadeIdeal(Math.max(0, Number(value)));
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseModalEditar}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="fluxo-atendimento__modal__box " id="modal__editarStock">
          <h1 className="fluxo-atendimento__modal__titulo">Editar Produto</h1>
          <div className="stockEditar__container">
            <div className="stockEditar__container__data">
              <TextField
                label="Nome do Produto"
                value={nomeProduto}
                onChange={(e) => setNomeProduto(e.target.value)}
                fullWidth
                margin="normal"
                required
              />

              <TextField
                select
                label="Medida"
                value={unidadeMedida}
                onChange={(e) => setUnidadeMedida(e.target.value)}
                fullWidth
                margin="normal"
              >
                {UNIDADES_MEDIDA.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Categoria Stock"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                fullWidth
                margin="normal"
                required
              >
                {categoriasStock.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.nome}
                  </MenuItem>
                ))}
              </TextField>

              <div className="stockEditar__container__row">
                <TextField
                  label={
                    unidades.length > 1
                      ? "Stock mínimo (toda a clínica)"
                      : "Stock mínimo"
                  }
                  type="number"
                  value={quantidadeIdeal}
                  onChange={handleQuantidadeIdealChange}
                  fullWidth
                  margin="normal"
                  inputProps={{ min: 0, step: 0.01 }}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel id="classificacao-label">
                    Classificação
                  </InputLabel>
                  <Select
                    labelId="classificacao-label"
                    value={classificacao}
                    label="Classificação"
                    onChange={(e) => setClassificacao(e.target.value)}
                  >
                    <MenuItem value="PA">Produto Acabado</MenuItem>
                    <MenuItem value="MP">Matéria Prima</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* Mínimos por unidade — só onde há mais do que uma.
                  Quem vê o stock de uma unidade vê as quantidades DESSA
                  unidade; compará-las com o mínimo da clínica inteira acendia
                  alertas falsos. Deixar em branco é legítimo: essa unidade
                  passa a usar o mínimo da clínica. */}
              {unidades.length > 1 && (
                <div className="stockEditar__minimos">
                  <h3 className="stockEditar__lotes-title">
                    Stock mínimo por unidade
                  </h3>
                  <p className="stockEditar__minimos-ajuda">
                    Em branco = usa o mínimo da clínica.
                  </p>
                  <div className="stockEditar__minimos-grid">
                    {unidades.map((u) => (
                      <TextField
                        key={u._id}
                        label={`${u.codigo} — ${u.nome}`}
                        type="number"
                        value={minimosPorUnidade[u._id] ?? ""}
                        onChange={(e) =>
                          setMinimosPorUnidade((prev) => ({
                            ...prev,
                            [u._id]: e.target.value,
                          }))
                        }
                        margin="normal"
                        inputProps={{ min: 0, step: 1 }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Seção de lotes e validades */}
              {produtoLotes.length > 0 && (
                <div className="stockEditar__lotes-section">
                  <h3 className="stockEditar__lotes-title">
                    Datas de Validade dos Lotes
                  </h3>
                  <div className="stockEditar__lotes-grid">
                    {produtoLotes.map((lote, index) => (
                      <div
                        key={lote._id || index}
                        className="stockEditar__lote-item"
                      >
                        <span className="stockEditar__lote-code">
                          Lote: {lote.lote} ({lote.quantidade} {unidadeMedida})
                          {lote.fornecedor && (
                            <span className="stockEditar__lote-fornecedor">
                              Fornecedor:{" "}
                              {lote.fornecedor.nome || "Desconhecido"}
                            </span>
                          )}
                        </span>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DesktopDatePicker
                            label="Data de Validade"
                            inputFormat="DD/MM/YYYY"
                            value={lote.dataValidade}
                            onChange={(date) => handleLoteValidade(date, index)}
                            renderInput={(params) => (
                              <TextField {...params} size="small" fullWidth />
                            )}
                          />
                        </LocalizationProvider>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {validationError && (
              <div className="modal__validationErrors__container">
                {validationError}
              </div>
            )}
            <div className="fluxo-atendimento__modal__bottom_btns">
              <span
                className="cancel-btn"
                onClick={handleCloseModalEditar}
                style={{ display: "block" }}
              >
                Fechar
              </span>

              <span
                disabled={isSubmitting}
                className="blue-button"
                onClick={preHandleSubmit}
              >
                {isSubmitting ? "Guardando..." : "Guardar"}
              </span>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default ModalEditarProduto;
